
import { useState, useEffect } from 'react';
import { articleService } from '@/services/article';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export const useArticleData = (articleId: string | undefined) => {
  const [article, setArticle] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const { user } = useAuth();
  
  useEffect(() => {
    const fetchArticle = async () => {
      // Reset state for new article load
      setIsLoading(true);
      setError(null);
      
      // Check if articleId is valid
      if (!articleId || articleId === 'NaN' || articleId === 'undefined') {
        setError('Invalid article ID');
        setIsLoading(false);
        return;
      }
      
      try {
        console.log(`Fetching article with ID: ${articleId}`);
        const { data, error } = await articleService.getArticleById(articleId);
        
        if (error) {
          console.error('Error fetching article:', error);
          setError('Could not load the article');
          toast({
            title: 'Error loading article',
            description: 'There was a problem loading this article',
            variant: 'destructive'
          });
          return;
        }
        
        if (!data) {
          setError('Article not found');
          return;
        }
        
        console.log('Article data loaded:', data);
        setArticle(data);
        
        // Check if user is following the author
        if (user && data?.author?.id) {
          console.log('Checking if user is following author:', data.author.id);
          const { data: followData } = await supabase
            .from('journalist_follows')
            .select('*')
            .eq('user_id', user.id)
            .eq('journalist_id', data.author.id);
            
          console.log('Follow data:', followData);
          setIsFollowing(followData && followData.length > 0);
          
          // Check if user has bookmarked the article
          const isBookmarked = await articleService.isArticleBookmarked(articleId);
          setIsBookmarked(isBookmarked);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setError('An unexpected error occurred');
        toast({
          title: 'Error',
          description: 'An unexpected error occurred while loading the article',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchArticle();
  }, [articleId, user]);
  
  const toggleFollowAuthor = async () => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to follow journalists',
        variant: 'destructive'
      });
      return;
    }
    
    if (!article?.author?.id) {
      toast({
        title: 'Error',
        description: 'Author information is not available',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const authorId = article.author.id;
      console.log('Toggling follow for author ID:', authorId);
      const authorName = article.author.name || 'this journalist';
      
      if (isFollowing) {
        // Unfollow the author
        const { error } = await supabase
          .from('journalist_follows')
          .delete()
          .eq('user_id', user.id)
          .eq('journalist_id', authorId);
          
        if (error) throw error;
        
        setIsFollowing(false);
        toast({
          title: `Unfollowed ${authorName}`,
          description: "You will no longer receive updates from this journalist."
        });
      } else {
        // Follow the author
        const { error } = await supabase
          .from('journalist_follows')
          .insert({
            user_id: user.id,
            journalist_id: authorId
          });
          
        if (error) throw error;
        
        setIsFollowing(true);
        toast({
          title: `Following ${authorName}`,
          description: "You will now receive updates when they publish new content."
        });
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
      toast({
        title: 'Action failed',
        description: 'There was a problem updating your follow status',
        variant: 'destructive'
      });
    }
  };
  
  const toggleBookmark = async () => {
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to bookmark articles',
        variant: 'destructive'
      });
      return;
    }
    
    if (!articleId) {
      toast({
        title: 'Error',
        description: 'Article information is not available',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      if (isBookmarked) {
        // Unsave the article
        const { success } = await articleService.unsaveArticle(articleId);
        
        if (success) {
          setIsBookmarked(false);
          toast({
            title: "Article removed from bookmarks",
            description: "The article has been removed from your saved items."
          });
        }
      } else {
        // Save the article
        const { success } = await articleService.saveArticle(articleId);
        
        if (success) {
          setIsBookmarked(true);
          toast({
            title: "Article bookmarked",
            description: "You can find this article in your bookmarks."
          });
        }
      }
    } catch (error) {
      console.error('Error toggling bookmark status:', error);
      toast({
        title: 'Action failed',
        description: 'There was a problem updating your bookmark',
        variant: 'destructive'
      });
    }
  };
  
  return {
    article,
    isLoading,
    error,
    isFollowing,
    isBookmarked,
    setIsFollowing: toggleFollowAuthor,
    toggleBookmark
  };
};
